import React, { useState, useCallback } from 'react';
import { Icon } from './Icon';
import Button from './Button';
import { useFavorites, usePendingRemovals } from '../../../stores/appStore';
import { useAnalytics } from '../../../contexts/AnalyticsContext';
import { Event } from '../../../types/event.types';
import '../../../styles/components/favorite-button.css';

interface FavoriteButtonProps {
  id: string;
  seriesId?: string;
  organizerId?: string;
  expiresAt?: Date;
  seriesEvents?: Event[];
  fullEvent?: Event;
  onFavoriteChange?: (isActive: boolean) => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  variant?: 'icon' | 'button';
  disabled?: boolean;
  analyticsContext?: 'card' | 'carte' | 'modale' | 'carte_preview';
  /** Index dans la liste pour l'animation (optionnel, utilisé dans FavoritesPage) */
  listIndex?: number;
}

const FavoriteButton: React.FC<FavoriteButtonProps> = ({
  id,
  seriesId,
  organizerId,
  expiresAt,
  seriesEvents,
  fullEvent,
  onFavoriteChange,
  size = 'md',
  className = '',
  variant = 'icon',
  disabled = false,
  analyticsContext,
  listIndex = 0
}) => {
  const storageId = seriesId || id;

  // Calculer la date d'expiration
  const calculatedExpiresAt = React.useMemo(() => {
    if (seriesEvents?.length) {
      const futureEvents = seriesEvents
        .filter(event => event.datetime && event.datetime > new Date())
        .sort((a, b) => {
          if (!a.datetime || !b.datetime) return 0;
          return b.datetime.getTime() - a.datetime.getTime();
        });
      return futureEvents[0]?.datetime || expiresAt;
    }
    return expiresAt;
  }, [seriesEvents, expiresAt]);

  const { isFavorite: isGloballyFavorite, toggleFavorite } = useFavorites();
  const { addPendingRemoval } = usePendingRemovals();
  const [isLoading, setIsLoading] = useState(false);
  const isFavorite = isGloballyFavorite(storageId);
  const analytics = useAnalytics();

  const handleClick = useCallback(async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (isLoading) return;

    setIsLoading(true);

    // Analytics tracking
    if (!isFavorite && analytics) {
      analytics.trackEventLike(
        { id, organizerId: organizerId || '' },
        'bouton_favori',
        analyticsContext || 'card'
      );
    }

    try {
      // Si on retire un favori, ajouter au pending removals pour l'animation
      if (isFavorite && fullEvent) {
        const eventTitle = fullEvent.title || 'Event';
        addPendingRemoval(storageId, eventTitle, fullEvent, listIndex);
      }

      // 🆕 Passer seriesEvents à toggleFavorite pour gérer les séries complètes
      await toggleFavorite(storageId, calculatedExpiresAt, onFavoriteChange, fullEvent, seriesEvents);
    } catch (error) {
      console.error('FavoriteButton - Error:', error);
    } finally {
      setIsLoading(false);
    }

    // Accessibility announcement
    const message = !isFavorite ? 'Added to favorites' : 'Removed from favorites';
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    document.body.appendChild(announcement);
    setTimeout(() => document.body.removeChild(announcement), 1000);
  }, [
    isLoading, isFavorite, analytics, id, organizerId, analyticsContext,
    fullEvent, storageId, listIndex, addPendingRemoval, toggleFavorite,
    calculatedExpiresAt, onFavoriteChange, seriesEvents
  ]);

  if (variant === 'button') {
    return (
      <Button
        variant={isFavorite ? "primary" : "outline"}
        size={size}
        onClick={handleClick}
        className={`${className} favorite-button-variant ${isFavorite ? 'active' : ''}`}
        disabled={isLoading}
      >
        <Icon
          name="Heart"
          size={20}
          className={isFavorite ? 'filled-heart' : ''}
        />
        {isFavorite ? 'Saved' : 'Save'}
      </Button>
    );
  }

  const classes = [
    'favorite-button',
    isFavorite ? 'active' : '',
    `favorite-button-${size}`,
    className
  ].filter(Boolean).join(' ');

  const iconSize = size === 'sm' ? 16 : size === 'lg' ? 24 : 20;

  return (
    <button
      className={classes}
      onClick={handleClick}
      type="button"
      aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      aria-pressed={isFavorite}
      disabled={disabled || isLoading}
    >
      <Icon
        name="Heart"
        size={iconSize}
        className={isFavorite ? 'filled-heart' : ''}
      />
    </button>
  );
};

export default FavoriteButton;
